import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useBalance } from '../context/BalanceContext';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import { motion } from 'framer-motion';
import {
  Landmark,
  ArrowLeftRight,
  History,
  ClipboardList,
  User,
  Wallet,
  Gavel,
  Info,
  LogOut,
  ChevronRight,
  Bell,
  Gift,
  Copy
} from 'lucide-react';

const ProfilePage = () => {
  const { user, token, logout } = useAuth();
  const { totalBalance, todayProfit, setTotalBalance, setTodayProfit, setDailyRatingCount, fetchBalance } = useBalance();
  const [totalCompletedProfit, setTotalCompletedProfit] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    fetchBalance();
    fetchTotalProfit();
  }, [fetchBalance, token]);

  const fetchTotalProfit = async () => {
    if (!token) return;
    try {
      const res = await axios.get('/api/profile/total-profit', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setTotalCompletedProfit(res.data.totalProfit || 0);
    } catch (err) {
      console.error('Error fetching total profit:', err);
      setTotalCompletedProfit(0);
    }
  };

  const handleLogout = () => {
    setTotalBalance(0);
    setTodayProfit(0.00);
    setDailyRatingCount(0);
    setTotalCompletedProfit(0);
    logout();
    navigate('/login');
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    // Add toast notification later
  };

  const StatItem = ({ label, value, isCode = false }) => (
    <div className="text-center">
      <p className="text-sm text-slate-400">{label}</p>
      {isCode ? (
        <div className="flex items-center justify-center gap-2 mt-1">
          <p className="text-lg font-mono font-bold text-white">{value}</p>
          <button onClick={() => copyToClipboard(value)} className="text-slate-400 hover:text-white">
            <Copy size={16} />
          </button>
        </div>
      ) : (
        <p className="text-lg font-bold text-white">{value}</p>
      )}
    </div>
  );

  const menuItems = {
    Financial: [
      { to: '/deposit', label: 'Deposit', icon: Landmark },
      { to: '/withdraw', label: 'Withdraw', icon: ArrowLeftRight },
      { to: '/transactions', label: 'Transaction History', icon: History },
      { to: '/records', label: 'Rating Records', icon: ClipboardList }
    ],
    Account: [
      { to: '/personal-info', label: 'Personal Information', icon: User },
      { to: '/withdraw-information', label: 'Withdraw Information', icon: Wallet },
      { to: '/notification', label: 'Notifications', icon: Bell },
      { to: '/invitation', label: 'Invitation', icon: Gift },
      { to: '/rules', label: 'Rules & Regulations', icon: Gavel },
      { to: '/about', label: 'About Us', icon: Info }
    ]
  };

  return (
    <>
    <Helmet>
      <title>My Profile | RatingApp</title>
      <meta name="description" content="View and manage your RatingApp profile, track your earnings, and access your account settings." />
    </Helmet>
    <div className="w-full pb-28 from-gray-900 via-blue-900/30 to-gray-900 text-white font-sans">
      <div className="max-w-2xl mx-auto px-4 py-8">
        {/* Profile Header */}
        <motion.div
          className="bg-gray-800/20 backdrop-blur-xl border border-white/10 rounded-3xl p-6 mb-8 text-center"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="flex flex-col items-center">
            <motion.img
              className="w-24 h-24 object-cover rounded-full border-4 border-white/20 shadow-lg mb-4"
              src={user && user.profilePictureUrl ? user.profilePictureUrl : `https://api.dicebear.com/7.x/initials/svg?seed=${user?.username || 'U'}`}
              alt="Profile"
              whileHover={{ scale: 1.05, rotate: 2 }}
            />
            <h2 className="text-2xl font-bold text-white">{user ? user.username : 'Loading...'}</h2>
          </div>
          <div className="mt-6 pt-6 border-t border-white/10 grid grid-cols-3 gap-4">
            <StatItem label="Invitation Code" value={user ? user.invitationCode : '...'} isCode />
            <StatItem label="Total Profit" value={`${totalCompletedProfit.toFixed(2)} USDT`} />
            <StatItem label="Total Balance" value={`${totalBalance.toFixed(2)} USDT`} />
          </div>
        </motion.div>
        
        {Object.entries(menuItems).map(([category, items], catIndex) => (
          <motion.div 
            key={category} 
            className="mb-8"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 + catIndex * 0.1 }}
          >
            <h3 className="text-slate-400 font-semibold mb-3 px-2">{category}</h3>
            <div className="bg-gray-800/20 backdrop-blur-xl border border-white/10 rounded-2xl">
              {items.map((item, itemIndex) => (
                <Link to={item.to} key={item.to}>
                  <motion.div
                    className="flex items-center justify-between px-5 py-4 w-full text-white transition-colors hover:bg-white/5"
                    style={{ borderBottom: itemIndex < items.length - 1 ? '1px solid rgba(255,255,255,0.1)' : 'none' }}
                    whileTap={{ scale: 0.98, backgroundColor: 'rgba(255,255,255,0.1)'}}
                  >
                    <div className="flex items-center gap-4">
                      <item.icon className="text-cyan-400" size={22} />
                      <span className="font-medium">{item.label}</span>
                    </div>
                    <ChevronRight className="text-slate-500" size={20} />
                  </motion.div>
          </Link>
              ))}
            </div>
          </motion.div>
        ))}

        {/* Logout Button */}
        <motion.div
          className="mt-6"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
        >
          <motion.button
            onClick={handleLogout}
            className="w-full bg-red-500/20 border border-red-500/30 text-red-400 font-bold py-3 rounded-xl flex items-center justify-center gap-2 transition-colors hover:bg-red-500/30"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <LogOut size={20} />
            Logout
          </motion.button>
        </motion.div>
      </div>
    </div>
    </>
  );
};

export default ProfilePage;
